import {
  type DynamicViewDefinition,
  type EntityFilterSchema,
  SYSTEM_VIEWS,
  type StaticViewDefinition,
  type SystemViewKind,
  type TableFilter,
  type WatchDataset,
  type WorkspaceViewDataset,
  type WorkspaceViewPresentation,
  parseDynamicViewDefinition,
} from '@parcel/filters';
import { toast } from '@parcel/ui';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Table } from '@tanstack/react-table';
import { ApiError, apiErrorCode, apiFetch } from './api.js';
import { invalidateFeed } from './feed.js';

export type WorkspaceView = {
  id: string;
  workspace_id: string;
  dataset: WorkspaceViewDataset;
  view_type: 'dynamic' | 'static';
  name: string;
  definition: DynamicViewDefinition | StaticViewDefinition;
  presentation: WorkspaceViewPresentation;
  revision: number;
  /** Members of a watched View feed the Feed and notifications. */
  watched: boolean;
  /** Set on the two Following Views, which cannot be renamed, redefined, unwatched or deleted. */
  system_kind: SystemViewKind | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  record_count?: number;
  is_favorite?: boolean;
  favorite_position?: number | null;
};

export type { SystemViewKind, WatchDataset };

export type WatchStatus = {
  followed: boolean;
  /** The Following View when the record is in it, else null. */
  system_view_id: string | null;
  via: { view_id: string; name: string; system_kind: SystemViewKind | null }[];
};

export type WatchWriteResult = {
  view: WorkspaceView | null;
  results: MembershipItemResult[];
  changed: number;
  succeeded: number;
  failed: number;
  still_followed?: { record_id: string; via: WatchStatus['via'] }[];
};

export type MembershipItemResult = {
  record_id: string;
  status: 'ok' | 'error';
  error?: {
    code: 'record_not_found' | 'record_not_visible';
    message: string;
  };
};

export type ViewUpsertInput =
  | {
      name: string;
      dataset: WorkspaceViewDataset;
      view_type: 'dynamic';
      definition: DynamicViewDefinition;
      presentation?: WorkspaceViewPresentation;
    }
  | {
      name: string;
      dataset: WorkspaceViewDataset;
      view_type: 'static';
      definition?: StaticViewDefinition;
      presentation?: WorkspaceViewPresentation;
    }
  | {
      id: string;
      expected_revision: number;
      name?: string;
      definition?: DynamicViewDefinition | StaticViewDefinition;
      presentation?: WorkspaceViewPresentation;
    }
  | { id: string; watched: boolean };

export type StaticCreateInput = {
  name: string;
  dataset: WorkspaceViewDataset;
  initial_record_ids: readonly string[];
  presentation?: WorkspaceViewPresentation;
};

export const workspaceViewKeys = {
  list: (workspaceId: string, dataset: WorkspaceViewDataset) =>
    ['workspace-views', workspaceId, dataset] as const,
  detail: (workspaceId: string, viewId: string) => ['workspace-view', workspaceId, viewId] as const,
  favorites: (workspaceId: string, userId: string) =>
    ['workspace-favorite-views', workspaceId, userId] as const,
  watchStatus: (workspaceId: string, dataset: WatchDataset, recordId: string) =>
    ['watch-status', workspaceId, dataset, recordId] as const,
};

/** React Query root key for each dataset's entity browse list. */
export const DATASET_ENTITY_LIST_QUERY_ROOT = {
  account: 'accounts',
  contact: 'contacts',
  project: 'projects',
} as const satisfies Record<WorkspaceViewDataset, string>;

type WorkspacePresentationApplicator<TData> = Pick<
  Table<TData>,
  'setSorting' | 'setColumnVisibility'
>;

export function serializeWorkspacePresentation<TData>(
  table: Table<TData>,
): WorkspaceViewPresentation {
  const state = table.getState();
  return {
    version: 1,
    sorting: state.sorting.map(({ id, desc }) => ({ id, desc })),
    column_visibility: { ...state.columnVisibility },
  };
}

export function applyWorkspacePresentation<TData>(
  table: WorkspacePresentationApplicator<TData>,
  presentation: WorkspaceViewPresentation,
) {
  table.setSorting(presentation.sorting);
  table.setColumnVisibility(presentation.column_visibility);
}

export function serializeWorkspaceView<TData>(
  dataset: WorkspaceViewDataset,
  table: Table<TData>,
  filter: TableFilter,
): {
  definition: DynamicViewDefinition;
  presentation: WorkspaceViewPresentation;
} {
  return {
    definition: parseDynamicViewDefinition(dataset, {
      version: 2,
      q: filter.q.trim(),
      where: filter.where,
    }),
    presentation: serializeWorkspacePresentation(table),
  };
}

export async function listWorkspaceViews(workspaceId: string, dataset: WorkspaceViewDataset) {
  const views: WorkspaceView[] = [];
  for (let offset = 0; ; offset += 100) {
    const query = new URLSearchParams({
      workspace_id: workspaceId,
      dataset,
      limit: '100',
      offset: String(offset),
    });
    const page = await apiFetch<{
      views: WorkspaceView[];
      pagination: { limit: number; offset: number };
    }>(`/views?${query}`);
    if (!Array.isArray(page.views)) {
      throw new Error('Expected flat member { views, pagination } response');
    }
    views.push(...page.views);
    if (page.views.length < 100) return views;
  }
}

export async function getWorkspaceView(workspaceId: string, viewId: string) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  const response = await apiFetch<{ view: WorkspaceView }>(
    `/views/${encodeURIComponent(viewId)}?${query}`,
  );
  return response.view;
}

export async function upsertWorkspaceView(workspaceId: string, input: ViewUpsertInput) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  const response = await apiFetch<{ view: WorkspaceView }>(`/views/upsert?${query}`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return response.view;
}

export async function deleteWorkspaceView(workspaceId: string, viewId: string) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  await apiFetch<{ deleted: true }>(`/views/${encodeURIComponent(viewId)}?${query}`, {
    method: 'DELETE',
  });
}

export async function createStaticWorkspaceView(workspaceId: string, input: StaticCreateInput) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  return apiFetch<{ view: WorkspaceView; results: MembershipItemResult[] }>(
    `/views/static-create?${query}`,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
}

export async function addWorkspaceViewRecords(
  workspaceId: string,
  viewId: string,
  recordIds: readonly string[],
) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  return apiFetch<{
    view_id: string;
    results: MembershipItemResult[];
    succeeded: number;
    failed: number;
  }>(`/views/${encodeURIComponent(viewId)}/records/add/batch?${query}`, {
    method: 'POST',
    body: JSON.stringify({ record_ids: recordIds }),
  });
}

export async function removeWorkspaceViewRecords(
  workspaceId: string,
  viewId: string,
  recordIds: readonly string[],
) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  return apiFetch<{
    view_id: string;
    results: MembershipItemResult[];
    succeeded: number;
    failed: number;
  }>(`/views/${encodeURIComponent(viewId)}/records/delete/batch?${query}`, {
    method: 'POST',
    body: JSON.stringify({ record_ids: recordIds }),
  });
}

export async function listFavoriteViews(workspaceId: string) {
  const views: WorkspaceView[] = [];
  for (let offset = 0; ; offset += 100) {
    const query = new URLSearchParams({
      workspace_id: workspaceId,
      limit: '100',
      offset: String(offset),
    });
    const page = await apiFetch<{
      favorite_views: WorkspaceView[];
      pagination: { limit: number; offset: number };
    }>(`/favorite-views?${query}`);
    if (!Array.isArray(page.favorite_views)) {
      throw new Error('Expected flat member { favorite_views, pagination } response');
    }
    views.push(...page.favorite_views);
    if (page.favorite_views.length < 100) return views;
  }
}

export async function upsertFavoriteView(
  workspaceId: string,
  input: { view_id: string; position?: number },
) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  const response = await apiFetch<{ favorite_view: WorkspaceView }>(
    `/favorite-views/upsert?${query}`,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
  return response.favorite_view;
}

export async function reorderFavoriteViews(workspaceId: string, viewIds: readonly string[]) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  const response = await apiFetch<{ favorite_views: WorkspaceView[] }>(
    `/favorite-views/reorder?${query}`,
    {
      method: 'POST',
      body: JSON.stringify({ view_ids: viewIds }),
    },
  );
  return response.favorite_views;
}

export async function deleteFavoriteView(workspaceId: string, viewId: string) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  await apiFetch<{ deleted: true }>(`/favorite-views/${encodeURIComponent(viewId)}?${query}`, {
    method: 'DELETE',
  });
}

export async function getWatchStatus(workspaceId: string, dataset: WatchDataset, recordId: string) {
  const query = new URLSearchParams({ workspace_id: workspaceId, dataset, record_id: recordId });
  return apiFetch<WatchStatus>(`/follow/status?${query}`);
}

export async function watchRecords(
  workspaceId: string,
  dataset: WatchDataset,
  recordIds: readonly string[],
) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  return apiFetch<WatchWriteResult>(`/follow/batch?${query}`, {
    method: 'POST',
    body: JSON.stringify({ dataset, record_ids: recordIds }),
  });
}

export async function unwatchRecords(
  workspaceId: string,
  dataset: WatchDataset,
  recordIds: readonly string[],
) {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  return apiFetch<WatchWriteResult>(`/unfollow/batch?${query}`, {
    method: 'POST',
    body: JSON.stringify({ dataset, record_ids: recordIds }),
  });
}

export function invalidateSharedViewCaches(
  queryClient: QueryClient,
  workspaceId: string,
  dataset?: WorkspaceViewDataset,
  viewId?: string,
) {
  if (dataset) {
    void queryClient.invalidateQueries({ queryKey: workspaceViewKeys.list(workspaceId, dataset) });
  } else {
    void queryClient.invalidateQueries({ queryKey: ['workspace-views', workspaceId] });
  }
  if (viewId) {
    void queryClient.invalidateQueries({
      queryKey: workspaceViewKeys.detail(workspaceId, viewId),
    });
  }
  void queryClient.invalidateQueries({ queryKey: ['workspace-favorite-views', workspaceId] });
  // Membership and watched flags decide follow status and the News feed; Suggested keeps its cards
  // until the next load, which reads it fresh.
  void queryClient.invalidateQueries({ queryKey: ['watch-status', workspaceId] });
  invalidateFeed(queryClient, workspaceId);
}

function viewErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const body = err.body as { error?: { message?: unknown } };
    if (typeof body.error?.message === 'string') return body.error.message;
  }
  return fallback;
}

export function useWorkspaceViews(
  workspaceId: string | undefined,
  dataset: WorkspaceViewDataset,
  /** Off for a caller that only wants the list once the member asks for it. */
  enabled = true,
) {
  return useQuery({
    queryKey: workspaceId
      ? workspaceViewKeys.list(workspaceId, dataset)
      : ['workspace-views', 'pending', dataset],
    enabled: Boolean(workspaceId) && enabled,
    queryFn: () => listWorkspaceViews(workspaceId ?? '', dataset),
  });
}

export function useWorkspaceView(workspaceId: string | undefined, viewId: string | null) {
  return useQuery({
    queryKey:
      workspaceId && viewId
        ? workspaceViewKeys.detail(workspaceId, viewId)
        : ['workspace-view', 'pending'],
    enabled: Boolean(workspaceId) && Boolean(viewId),
    queryFn: () => getWorkspaceView(workspaceId ?? '', viewId ?? ''),
  });
}

export function useFavoriteViews(workspaceId: string | undefined, userId: string | undefined) {
  return useQuery({
    queryKey:
      workspaceId && userId
        ? workspaceViewKeys.favorites(workspaceId, userId)
        : ['workspace-favorite-views', 'pending'],
    enabled: Boolean(workspaceId) && Boolean(userId),
    queryFn: () => listFavoriteViews(workspaceId ?? ''),
  });
}

export function useWorkspaceViewMutations(
  workspaceId: string | undefined,
  dataset: WorkspaceViewDataset,
) {
  const queryClient = useQueryClient();

  const invalidateShared = (viewId?: string) => {
    if (!workspaceId) return;
    invalidateSharedViewCaches(queryClient, workspaceId, dataset, viewId);
  };

  const upsert = useMutation({
    mutationFn: async (input: ViewUpsertInput) => {
      if (!workspaceId) throw new Error('Cannot upsert a View without a workspace');
      return upsertWorkspaceView(workspaceId, input);
    },
    onSuccess: (view) => {
      invalidateShared(view.id);
    },
    onError: (err) => {
      if (apiErrorCode(err) === 'view_conflict') return;
      console.error('[views] upsert failed', err);
      toast.error(viewErrorMessage(err, "Couldn't save the View — please try again."));
    },
  });

  const remove = useMutation({
    mutationFn: async (viewId: string) => {
      if (!workspaceId) throw new Error('Cannot delete a View without a workspace');
      await deleteWorkspaceView(workspaceId, viewId);
      return viewId;
    },
    onSuccess: (viewId) => {
      invalidateShared(viewId);
    },
    onError: (err) => {
      console.error('[views] delete failed', err);
      toast.error(viewErrorMessage(err, "Couldn't delete the View — please try again."));
    },
  });

  const staticCreate = useMutation({
    mutationFn: async (input: StaticCreateInput) => {
      if (!workspaceId) throw new Error('Cannot create a Static View without a workspace');
      return createStaticWorkspaceView(workspaceId, input);
    },
    onSuccess: ({ view }) => {
      invalidateShared(view.id);
    },
    onError: (err) => {
      console.error('[views] static create failed', err);
      toast.error(viewErrorMessage(err, "Couldn't create the View — please try again."));
    },
  });

  const addRecords = useMutation({
    mutationFn: async (input: { viewId: string; recordIds: readonly string[] }) => {
      if (!workspaceId) throw new Error('Cannot update View membership without a workspace');
      return addWorkspaceViewRecords(workspaceId, input.viewId, input.recordIds);
    },
    onSuccess: (result) => {
      invalidateShared(result.view_id);
      if (workspaceId) {
        void queryClient.invalidateQueries({
          queryKey: [DATASET_ENTITY_LIST_QUERY_ROOT[dataset], workspaceId],
        });
      }
    },
    onError: (err) => {
      console.error('[views] add records failed', err);
      toast.error(viewErrorMessage(err, "Couldn't add records — please try again."));
    },
  });

  const removeRecords = useMutation({
    mutationFn: async (input: { viewId: string; recordIds: readonly string[] }) => {
      if (!workspaceId) throw new Error('Cannot update View membership without a workspace');
      return removeWorkspaceViewRecords(workspaceId, input.viewId, input.recordIds);
    },
    onSuccess: (result) => {
      invalidateShared(result.view_id);
      if (workspaceId) {
        void queryClient.invalidateQueries({
          queryKey: [DATASET_ENTITY_LIST_QUERY_ROOT[dataset], workspaceId],
        });
      }
    },
    onError: (err) => {
      console.error('[views] remove records failed', err);
      toast.error(viewErrorMessage(err, "Couldn't remove records — please try again."));
    },
  });

  return { upsert, remove, staticCreate, addRecords, removeRecords };
}

export function useFavoriteViewMutations(
  workspaceId: string | undefined,
  userId: string | undefined,
) {
  const queryClient = useQueryClient();
  const favoritesKey =
    workspaceId && userId
      ? workspaceViewKeys.favorites(workspaceId, userId)
      : (['workspace-favorite-views', 'pending'] as const);

  const favorite = useMutation({
    mutationFn: async (input: { view_id: string; position?: number }) => {
      if (!workspaceId) throw new Error('Cannot favorite a View without a workspace');
      return upsertFavoriteView(workspaceId, input);
    },
    onMutate: async (input) => {
      if (!workspaceId || !userId) return;
      await queryClient.cancelQueries({ queryKey: favoritesKey });
      const previous = queryClient.getQueryData<WorkspaceView[]>(favoritesKey);
      queryClient.setQueryData<WorkspaceView[]>(favoritesKey, (current = []) => {
        const existing = current.find((view) => view.id === input.view_id);
        if (existing) {
          return current.map((view) =>
            view.id === input.view_id
              ? {
                  ...view,
                  is_favorite: true,
                  favorite_position: input.position ?? view.favorite_position ?? current.length,
                }
              : view,
          );
        }
        const fromLists = queryClient
          .getQueriesData<WorkspaceView[]>({ queryKey: ['workspace-views', workspaceId] })
          .flatMap(([, views]) => views ?? [])
          .find((view) => view.id === input.view_id);
        if (!fromLists) return current;
        return [
          ...current,
          {
            ...fromLists,
            is_favorite: true,
            favorite_position: input.position ?? current.length,
          },
        ];
      });
      return { previous };
    },
    onError: (err, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(favoritesKey, context.previous);
      }
      console.error('[views] favorite failed', err);
      toast.error(viewErrorMessage(err, "Couldn't favorite the View — please try again."));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: favoritesKey });
    },
  });

  const unfavorite = useMutation({
    mutationFn: async (viewId: string) => {
      if (!workspaceId) throw new Error('Cannot unfavorite a View without a workspace');
      await deleteFavoriteView(workspaceId, viewId);
      return viewId;
    },
    onMutate: async (viewId) => {
      if (!workspaceId || !userId) return;
      await queryClient.cancelQueries({ queryKey: favoritesKey });
      const previous = queryClient.getQueryData<WorkspaceView[]>(favoritesKey);
      queryClient.setQueryData<WorkspaceView[]>(favoritesKey, (current = []) =>
        current.filter((view) => view.id !== viewId),
      );
      return { previous };
    },
    onError: (err, _viewId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(favoritesKey, context.previous);
      }
      console.error('[views] unfavorite failed', err);
      toast.error(viewErrorMessage(err, "Couldn't unfavorite the View — please try again."));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: favoritesKey });
    },
  });

  const reorder = useMutation({
    mutationFn: async (viewIds: readonly string[]) => {
      if (!workspaceId) throw new Error('Cannot reorder Favorites without a workspace');
      return reorderFavoriteViews(workspaceId, viewIds);
    },
    onMutate: async (viewIds) => {
      if (!workspaceId || !userId) return;
      await queryClient.cancelQueries({ queryKey: favoritesKey });
      const previous = queryClient.getQueryData<WorkspaceView[]>(favoritesKey);
      const byId = new Map((previous ?? []).map((view) => [view.id, view]));
      queryClient.setQueryData<WorkspaceView[]>(favoritesKey, () => {
        const next: WorkspaceView[] = [];
        for (const [index, id] of viewIds.entries()) {
          const view = byId.get(id);
          if (!view) continue;
          next.push({ ...view, favorite_position: index, is_favorite: true });
        }
        return next;
      });
      return { previous };
    },
    onError: (err, _viewIds, context) => {
      if (context?.previous) {
        queryClient.setQueryData(favoritesKey, context.previous);
      }
      console.error('[views] reorder favorites failed', err);
      toast.error(viewErrorMessage(err, "Couldn't reorder Favorites — please try again."));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: favoritesKey });
    },
  });

  return { favorite, unfavorite, reorder };
}

/** Everything a follow or unfollow can change: status, Views, Favorites and the table. */
export function invalidateWatchCaches(
  queryClient: QueryClient,
  workspaceId: string | undefined,
  dataset: WatchDataset,
) {
  if (!workspaceId) return;
  invalidateSharedViewCaches(queryClient, workspaceId, dataset);
  void queryClient.invalidateQueries({ queryKey: ['workspace-view', workspaceId] });
  void queryClient.invalidateQueries({
    queryKey: [DATASET_ENTITY_LIST_QUERY_ROOT[dataset], workspaceId],
  });
}

export function useWatchStatus(
  workspaceId: string | undefined,
  dataset: WatchDataset,
  recordId: string | undefined,
) {
  return useQuery({
    queryKey:
      workspaceId && recordId
        ? workspaceViewKeys.watchStatus(workspaceId, dataset, recordId)
        : ['watch-status', 'pending', dataset],
    enabled: Boolean(workspaceId) && Boolean(recordId),
    queryFn: () => getWatchStatus(workspaceId ?? '', dataset, recordId ?? ''),
  });
}

/** Follow and unfollow; a single record flips its status optimistically. */
export function useWatchMutations(workspaceId: string | undefined, dataset: WatchDataset) {
  const queryClient = useQueryClient();

  const settle = () => invalidateWatchCaches(queryClient, workspaceId, dataset);

  const statusKey = (recordIds: readonly string[]) =>
    workspaceId && recordIds.length === 1
      ? workspaceViewKeys.watchStatus(workspaceId, dataset, recordIds[0] as string)
      : null;

  /** The status once the record is (or is not) in the Following View, member Views kept. */
  const withSystemView = (current: WatchStatus | undefined, systemViewId: string | null) => {
    const via = (current?.via ?? []).filter((entry) => entry.system_kind === null);
    if (systemViewId === null) return { followed: via.length > 0, system_view_id: null, via };
    const { kind, name } = SYSTEM_VIEWS[dataset];
    return {
      followed: true,
      system_view_id: systemViewId,
      via: [{ view_id: systemViewId, name, system_kind: kind }, ...via],
    };
  };

  const flip = async (recordIds: readonly string[], followed: boolean) => {
    const key = statusKey(recordIds);
    if (!key) return undefined;
    await queryClient.cancelQueries({ queryKey: key });
    const previous = queryClient.getQueryData<WatchStatus>(key);
    // The Following View's id is unknown until the write answers; '' marks it pending.
    queryClient.setQueryData<WatchStatus>(key, (current) =>
      withSystemView(current, followed ? (current?.system_view_id ?? '') : null),
    );
    return { key, previous };
  };

  const rollback = (context: Awaited<ReturnType<typeof flip>>) => {
    if (context) queryClient.setQueryData(context.key, context.previous);
  };

  const follow = useMutation({
    mutationFn: async (recordIds: readonly string[]) => {
      if (!workspaceId) throw new Error('Cannot follow without a workspace');
      return watchRecords(workspaceId, dataset, recordIds);
    },
    onMutate: (recordIds) => flip(recordIds, true),
    onSuccess: (result, recordIds, context) => {
      const key = statusKey(recordIds);
      const refused = key ? result.results[0]?.error : undefined;
      if (refused) {
        // A 200 can still refuse the one record; put the status back and say why.
        rollback(context);
        toast.error(refused.message);
        return;
      }
      if (key && result.view) {
        queryClient.setQueryData<WatchStatus>(key, (current) =>
          withSystemView(current, result.view?.id ?? null),
        );
      }
    },
    onError: (err, _recordIds, context) => {
      rollback(context);
      console.error('[views] follow failed', err);
      toast.error(viewErrorMessage(err, "Couldn't follow — please try again."));
    },
    onSettled: settle,
  });

  const unfollow = useMutation({
    mutationFn: async (recordIds: readonly string[]) => {
      if (!workspaceId) throw new Error('Cannot unfollow without a workspace');
      return unwatchRecords(workspaceId, dataset, recordIds);
    },
    onMutate: (recordIds) => flip(recordIds, false),
    onError: (err, _recordIds, context) => {
      rollback(context);
      console.error('[views] unfollow failed', err);
      toast.error(viewErrorMessage(err, "Couldn't unfollow — please try again."));
    },
    onSettled: settle,
  });

  return { follow, unfollow };
}

/** @deprecated Prefer EntityFilterSchema from the dataset registry at call sites. */
export type WorkspaceViewSchema = EntityFilterSchema;
